import React, { useState, memo, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { useZoomControls } from '../../hooks/useZoomControls';
import ImageControlBar from './ImageControlBar';
import ImageContainer from './ImageContainer';
import { TransformState } from '../../types/imageTransform';

interface ZoomableImageProps {
  imageUrl: string | null;
  onClose: () => void;
  hoveredRowIndex?: number | null;
  onRowHover?: (index: number | null) => void;
  onGuideLineClientYChange?: (clientY: number | null) => void;
  showHoverGuideLine?: boolean;
  showGuideGrid?: boolean;
  guideGridOriginClientY?: number | null;
  ticketCount?: number;
  onEditReceipt?: () => void;
}

const ZoomableImage = memo<ZoomableImageProps>(({
  imageUrl,
  onClose,
  hoveredRowIndex,
  onRowHover,
  onGuideLineClientYChange,
  showHoverGuideLine,
  showGuideGrid,
  guideGridOriginClientY,
  ticketCount,
  onEditReceipt,
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggable, setIsDraggable] = useState(false);
  const [hasBeenMoved, setHasBeenMoved] = useState(false);
  const [isZoomLocked, setIsZoomLocked] = useState(false);
  const [initialFitScale, setInitialFitScale] = useState(1);
  
  const dragOrigin = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const originalDimensions = useRef({ width: 0, height: 0 });
  const lastScrollPosition = useRef({ x: 0, y: 0 });
  const originalPosition = useRef({ x: 0, y: 0 });
  const originalParent = useRef<HTMLElement | null>(null);

  // Use the extracted zoom controls hook
  const {
    scale,
    rotation,
    zoomIn,
    zoomOut,
    zoomInStart,
    zoomOutStart,
    zoomStop,
    rotateLeft,
    rotateRight,
    setScale,
    setRotation,
  } = useZoomControls({
    isZoomLocked,
    onTransformChange: (newScale, newRotation) => {
      // This callback can be used for additional transform logic if needed
    },
    onPositionChange: (newPosition) => {
      setPosition(newPosition);
    },
    currentPosition: position,
    containerRef,
  });

  // Alternative approach: wait for container to be fully rendered
  useEffect(() => {
    if (!imageUrl || !containerRef.current) return;

    const checkContainerReady = () => {
      const container = containerRef.current;
      if (!container) return;

      // Wait for container to have proper dimensions
      if (container.offsetWidth > 0) {
        const containerWidth = container.offsetWidth;
        
        const img = new Image();
        img.onload = () => {
          const imageWidth = img.naturalWidth;
          const initialScale = Math.min(containerWidth / imageWidth, 1);
          setInitialFitScale(initialScale);
          setScale(initialScale);
        };
        img.src = imageUrl;
      } else {
        // Container not ready yet, try again
        setTimeout(checkContainerReady, 50);
      }
    };

    // Start checking after a short delay
    setTimeout(checkContainerReady, 200);
  }, [imageUrl, setScale]);

  // Track original position when component mounts
  useEffect(() => {
    const container = containerRef.current;
    if (container && !hasBeenMoved) {
      originalParent.current = container.parentElement;
      const rect = container.getBoundingClientRect();
      originalPosition.current = {
        x: rect.left,
        y: rect.top,
      };
    }
  }, []);

  // Update original position when component is in natural position and page layout changes
  useEffect(() => {
    if (hasBeenMoved) return;
    
    const updateOriginalPosition = () => {
      const container = containerRef.current;
      if (container && container.style.position !== 'fixed') {
        const rect = container.getBoundingClientRect();
        originalPosition.current = {
          x: rect.left,
          y: rect.top,
        };
      }
    };

    window.addEventListener('scroll', updateOriginalPosition);
    window.addEventListener('resize', updateOriginalPosition);
    
    return () => {
      window.removeEventListener('scroll', updateOriginalPosition);
      window.removeEventListener('resize', updateOriginalPosition);
    };
  }, [hasBeenMoved]);

  const handleReturnToDefaultPosition = (e: React.MouseEvent) => {
    e.stopPropagation();
    const container = containerRef.current;
    if (container && hasBeenMoved && originalParent.current) {
      originalParent.current.appendChild(container);
      
      container.style.position = '';
      container.style.top = '';
      container.style.left = '';
      container.style.zIndex = '';
      container.style.width = '';
      container.style.height = '';
      
      setHasBeenMoved(false);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleToggleZoomLock = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsZoomLocked(prev => !prev);
    if (!isZoomLocked) {
      zoomStop();
    }
  };

  const handleResetZoom = (e: React.MouseEvent) => {
    e.stopPropagation();
    setScale(initialFitScale);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleToggleZoom = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (scale > initialFitScale) {
      // If zoomed in, reset to initial fit scale
      setScale(initialFitScale);
      setPosition({ x: 0, y: 0 });
    } else {
      // If at initial fit scale, zoom in to 2x
      setScale(2);
    }
  };

  // Drag logic using window listeners
  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e: MouseEvent) => {
      e.preventDefault();
      setPosition({
        x: e.clientX - dragOrigin.current.x,
        y: e.clientY - dragOrigin.current.y,
      });
    };
    const handleUp = (e: MouseEvent) => {
      e.preventDefault();
      setIsDragging(false);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging]);

  // Global drag logic for the entire component
  useEffect(() => {
    if (!isDraggable) return;
    
    const handleMove = (e: MouseEvent) => {
      e.preventDefault();
      const container = containerRef.current;
      if (container) {
        const newX = e.clientX - dragOrigin.current.x;
        const newY = e.clientY - dragOrigin.current.y;
        
        container.style.position = 'fixed';
        container.style.top = `${newY}px`;
        container.style.left = `${newX}px`;
        container.style.zIndex = '200';
        container.style.width = `${originalDimensions.current.width}px`;
        container.style.height = `${originalDimensions.current.height}px`;
        
        setHasBeenMoved(true);
        lastScrollPosition.current = { x: window.scrollX, y: window.scrollY };
      }
    };
    
    const handleUp = (e: MouseEvent) => {
      e.preventDefault();
      setIsDraggable(false);
    };
    
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggable]);

  // Persistent scroll handler for moved components
  useEffect(() => {
    if (!hasBeenMoved) return;
    
    const handleScroll = () => {
      const container = containerRef.current;
      if (container && container.style.position === 'fixed') {
        const scrollDeltaY = window.scrollY - lastScrollPosition.current.y;
        const scrollDeltaX = window.scrollX - lastScrollPosition.current.x;
        const currentTop = parseFloat(container.style.top) || 0;
        const currentLeft = parseFloat(container.style.left) || 0;
        
        container.style.top = `${currentTop - scrollDeltaY}px`;
        container.style.left = `${currentLeft - scrollDeltaX}px`;
        
        lastScrollPosition.current = { x: window.scrollX, y: window.scrollY };
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasBeenMoved]);

  const handleGlobalDragStart = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggable(true);
    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      dragOrigin.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      originalDimensions.current = {
        width: rect.width,
        height: rect.height,
      };
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragOrigin.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (isZoomLocked) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.deltaY < 0) {
      zoomIn(e);
    } else {
      zoomOut(e);
    }
  };

  // Attach native wheel event to prevent page scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const wheelHandler = (e: WheelEvent) => {
      handleWheel(e as any);
      e.preventDefault();
    };
    el.addEventListener('wheel', wheelHandler, { passive: false });
    return () => {
      el.removeEventListener('wheel', wheelHandler);
    };
  });

  if (!imageUrl) return null;

  // Create transform object for the image container
  const transform: TransformState = {
    scale,
    rotation,
    position,
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        border: '1px solid #ddd',
        borderRadius: 1,
        bgcolor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <ImageControlBar
        onEditReceipt={onEditReceipt}
        scale={scale}
        isZoomLocked={isZoomLocked}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onZoomInStart={zoomInStart}
        onZoomOutStart={zoomOutStart}
        onZoomStop={zoomStop}
        onRotateLeft={rotateLeft}
        onRotateRight={rotateRight}
        onReset={handleResetZoom}
        onToggleLock={handleToggleZoomLock}
        onReturnHome={handleReturnToDefaultPosition}
        onDragStart={handleGlobalDragStart}
      />

      <ImageContainer
        imageUrl={imageUrl}
        transform={transform}
        isDragging={isDragging}
        onDoubleClick={handleToggleZoom}
        onMouseDown={handleMouseDown}
        hoveredRowIndex={hoveredRowIndex}
        onRowHover={onRowHover}
        onGuideLineClientYChange={onGuideLineClientYChange}
        showHoverGuideLine={showHoverGuideLine}
        showGuideGrid={showGuideGrid}
        guideGridOriginClientY={guideGridOriginClientY}
        ticketCount={ticketCount}
      />
    </Box>
  );
});

ZoomableImage.displayName = 'ZoomableImage';

export default ZoomableImage; 
